import { BadgeCheck, Folder, Gauge, History, LayoutDashboard, ShieldCheck } from 'lucide-react'
import { Link, useParams, useSearchParams } from 'wouter'
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  DetailListSkeleton,
  Section,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  PageHeader,
} from '@ideon-api/ideon-ui'
import { useAccountDetail } from '../lib/use-account-detail'
import { FilesTab } from './account-detail/FilesTab'
import { HistorySkeleton, HistoryTab } from './account-detail/HistoryTab'
import { LicensingSkeleton, LicensingTab } from './account-detail/LicensingTab'
import { OverviewTab } from './account-detail/OverviewTab'
import { PermissionSetSelector } from './account-detail/PermissionSetSelector'
import { PrivilegedSkeleton, PrivilegedTab } from './account-detail/PrivilegedTab'
import { RateLimitsSkeleton, RateLimitsTab } from './account-detail/RateLimitsTab'

const TABS = [
  { value: 'overview', label: 'Overview', Icon: LayoutDashboard },
  { value: 'licensing', label: 'Licensing', Icon: BadgeCheck },
  { value: 'privileged', label: 'Privileged', Icon: ShieldCheck },
  { value: 'rate-limits', label: 'Rate limits', Icon: Gauge },
  { value: 'files', label: 'Files', Icon: Folder },
  { value: 'history', label: 'History', Icon: History },
] as const

type TabValue = (typeof TABS)[number]['value']

// Tabs whose content is scoped to one permission set.
const SET_SCOPED_TABS: ReadonlyArray<TabValue> = ['licensing', 'privileged', 'files']

function parseTab(raw: string | null): TabValue {
  return TABS.find((t) => t.value === raw)?.value ?? 'overview'
}

export function AccountDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const state = useAccountDetail(slug)

  const tab = parseTab(searchParams.get('tab'))
  const setParam = (key: string, value: string) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set(key, value)
        return params
      },
      { replace: true }
    )

  const data = state.status === 'ready' ? state.data : null
  const name = data?.summary?.name || slug
  const sets = data?.permissionSets ?? []
  const defaultKey = (sets.find((s) => s.default) ?? sets[0])?.key ?? ''
  const requestedSet = searchParams.get('permission_set') || defaultKey
  const knownSet = sets.some((s) => s.key === requestedSet)
  const showSelector = SET_SCOPED_TABS.includes(tab) && sets.length > 0

  return (
    <>
      <PageHeader
        title={name}
        meta={<span className="text-sm text-ideon-gray-70">{slug}</span>}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link href="/accounts">Accounts</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbPage>{name}</BreadcrumbPage>
            </BreadcrumbItem>
          </Breadcrumb>
        }
      >
        {/* The row is reserved on every tab so the tab bar holds its y position. */}
        {state.status === 'error' ? undefined : showSelector ? (
            <PermissionSetSelector
              permissionSets={sets}
              value={knownSet ? requestedSet : ''}
              onChange={(key) =>
                setSearchParams(
                  (prev) => {
                    const params = new URLSearchParams(prev)
                    params.set('permission_set', key)
                    // Per-set view state must not carry into the next set.
                    for (const k of ['year', 'line', 'quarter', 'adapter', 'audience', 'state']) params.delete(k)
                    return params
                  },
                  { replace: true }
                )
              }
            />
          ) : null}
      </PageHeader>

      {state.status === 'error' &&
        (state.mergedInto ? (
          <Alert variant="info">
            {`'${slug}' was merged into `}
            <Link href={`/accounts/${state.mergedInto}`} className="underline">
              {state.mergedInto}
            </Link>
            {' →'}
          </Alert>
        ) : (
          <Alert variant="error">Failed to load: {state.message}</Alert>
        ))}

      {state.status !== 'error' && (
        <>
          {data && showSelector && !knownSet && (
            <Alert variant="warning" className="mb-4">
              {`Unknown permission set '${requestedSet}'.`}
            </Alert>
          )}

          {/* The tab bar renders while loading (triggers disabled) so the page does not shift when data lands. */}
          <Tabs value={tab} activationMode="manual" onValueChange={(next) => setParam('tab', next)}>
            <TabsList>
              {TABS.map((t) => (
                <TabsTrigger key={t.value} value={t.value} disabled={!data}>
                  <t.Icon aria-hidden />
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="overview" className="pt-6">
              {data ? <OverviewTab slug={slug} data={data} /> : <OverviewSkeleton />}
            </TabsContent>
            <TabsContent value="licensing" className="pt-6">
              {data ? knownSet && <LicensingTab key={requestedSet} permissionSetKey={requestedSet} /> : <LicensingSkeleton />}
            </TabsContent>
            <TabsContent value="privileged" className="pt-6">
              {data ? knownSet && <PrivilegedTab key={requestedSet} permissionSetKey={requestedSet} /> : <PrivilegedSkeleton />}
            </TabsContent>
            <TabsContent value="rate-limits" className="pt-6">
              {data ? (
                <RateLimitsTab
                  applicationIds={data.threeScaleApplications
                    .filter((app) => app.environment === 'production')
                    .map((app) => app.applicationId)}
                />
              ) : (
                <RateLimitsSkeleton />
              )}
            </TabsContent>
            <TabsContent value="files" className="pt-6">
              {knownSet && (
                <FilesTab
                  key={requestedSet}
                  permissionSetKey={requestedSet}
                  adapters={sets.find((s) => s.key === requestedSet)?.adapters ?? []}
                />
              )}
            </TabsContent>
            <TabsContent value="history" className="pt-6">
              {data ? (
                <HistoryTab slug={slug} permissionSets={sets} />
              ) : (
                <HistorySkeleton />
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </>
  )
}

const SKELETON_SECTIONS = [
  { heading: 'Account info', rows: 4 },
  { heading: 'Permission sets', rows: 2 },
  { heading: 'API access', rows: 2 },
]

// Mirrors OverviewTab's sections so the page height holds steady when data arrives.
function OverviewSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      {SKELETON_SECTIONS.map(({ heading, rows }) => (
        <Section key={heading} heading={heading} density="compact" bordered>
          <DetailListSkeleton rows={rows} />
        </Section>
      ))}
    </div>
  )
}
